// Standalone pin toggle for tool page headers.
// Mirrors the PinToggle inside ToolCard.tsx but styled for a page header context.

import { useState } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { useAddPin, usePins, useRemovePin } from '@/lib/usePins';
import { useSession } from '@/lib/useSession';

type Props = {
  toolSlug: string;
  toolName: string;
};

export function ToolPinToggle({ toolSlug, toolName }: Props) {
  const { user } = useSession();
  // Pinning writes to the pins API, which requires core:write. Store users (and
  // any principal without core:write) would get a 403, so hide the control for
  // them rather than show a button that fails on click.
  const canPin = !!user?.permissions?.includes('core:write');
  const { pinnedSet } = usePins();
  const pinned = pinnedSet.has(toolSlug);
  const addPin = useAddPin();
  const removePin = useRemovePin();
  const [error, setError] = useState<string | null>(null);

  const Icon = pinned ? Pin : PinOff;
  const label = pinned ? `Unpin ${toolName} from sidebar` : `Pin ${toolName} to sidebar`;
  const busy = addPin.isPending || removePin.isPending;

  const handleClick = () => {
    if (busy) return;
    const op = pinned ? removePin : addPin;
    op.mutate(toolSlug, {
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'Pin update failed';
        setError(msg);
        window.setTimeout(() => setError(null), 4000);
      },
    });
  };

  // Guard after all hooks so the hook order is stable across renders.
  if (!canPin) return null;

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={handleClick}
        aria-label={label}
        aria-pressed={pinned}
        title={label}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-amp-line text-amp-ink-muted transition-colors hover:bg-amp-cream-warm hover:text-amp-olive focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:border-amp-dark-line dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm dark:hover:text-amp-olive-dark"
        style={{ opacity: busy ? 0.5 : 1, cursor: busy ? 'wait' : 'pointer' }}
      >
        <Icon aria-hidden size={14} fill={pinned ? 'currentColor' : 'none'} strokeWidth={pinned ? 2 : 1.75} />
      </button>
      {error ? (
        <span
          role="status"
          className="absolute top-full left-0 mt-1 z-10 whitespace-nowrap rounded-md bg-red-50 px-2 py-1 text-red-700 dark:bg-red-900/40 dark:text-red-200"
          style={{ fontSize: 11, fontWeight: 500 }}
        >
          {error}
        </span>
      ) : null}
    </span>
  );
}
